import { Localized } from "@fluent/react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AuthPageLayout } from "./AuthPageLayout";
import { useAppDispatch } from "./app/hooks";
import { confirmSsoRegistration } from "./features/user/userSlice";
import { LanguageSelector } from "./LanguageSelector";

type ViewState = "loading" | "success" | "error";

const SsoConfirmAccount = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const [viewState, setViewState] = useState<ViewState>("loading");

    const userId = searchParams.get("u") ?? searchParams.get("userId") ?? "";
    const token = searchParams.get("t") ?? searchParams.get("token") ?? "";

    useEffect(() => {
        const confirm = async () => {
            if (!userId || !token) {
                setViewState("error");
                return;
            }

            try {
                await dispatch(confirmSsoRegistration({
                    userId,
                    token,
                })).unwrap();

                setViewState("success");
            } catch {
                setViewState("error");
            }
        };

        confirm();
    }, [dispatch, token, userId]);

    return (
        <AuthPageLayout>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    py: 3,
                    px: 1,
                    gap: 2,
                }}
            >
                {viewState === "loading" && (
                    <>
                        <CircularProgress />
                        <Typography variant="body1" color="text.secondary">
                            <Localized id="sso-confirm-account-loading">
                                <span>Confirming your account...</span>
                            </Localized>
                        </Typography>
                    </>
                )}

                {viewState === "success" && (
                    <>
                        <CheckCircleIcon sx={{ fontSize: 72, color: "success.main" }} />
                        <Typography variant="h5" fontWeight={700}>
                            <Localized id="sso-confirm-account-success-title">
                                <span>Account confirmed</span>
                            </Localized>
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            <Localized id="sso-confirm-account-success-message">
                                <span>Your account has been confirmed. You can now sign in with Microsoft SSO.</span>
                            </Localized>
                        </Typography>
                        <Button
                            component={Link}
                            to="/login"
                            size="large"
                            variant="contained"
                            color="primary"
                        >
                            <Localized id="back-to-login">
                                <span>Back to login</span>
                            </Localized>
                        </Button>
                        <Box sx={{ width: "100%", pt: 1 }}>
                            <LanguageSelector />
                        </Box>
                    </>
                )}

                {viewState === "error" && (
                    <>
                        <ErrorOutlineIcon sx={{ fontSize: 72, color: "error.main" }} />
                        <Typography variant="h5" fontWeight={700}>
                            <Localized id="sso-confirm-account-error-title">
                                <span>Confirmation failed</span>
                            </Localized>
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            <Localized id="sso-confirm-account-error-message">
                                <span>The confirmation link is invalid or expired.</span>
                            </Localized>
                        </Typography>
                        <Button
                            component={Link}
                            to="/login"
                            size="large"
                            variant="contained"
                            color="primary"
                        >
                            <Localized id="back-to-login">
                                <span>Back to login</span>
                            </Localized>
                        </Button>
                        <Box sx={{ width: "100%", pt: 1 }}>
                            <LanguageSelector />
                        </Box>
                    </>
                )}

                {viewState === "loading" && (
                    <Box sx={{ width: "100%", pt: 1 }}>
                        <LanguageSelector />
                    </Box>
                )}
            </Box>
        </AuthPageLayout>
    );
};

export default SsoConfirmAccount;